import React from 'react';
import { Box, Typography, Grid, Paper, Chip, Divider } from '@mui/material';
import { MdTrendingUp, MdTrendingDown, MdSportsScore, MdTimer } from 'react-icons/md';
import { BsTrophy, BsCalendarEvent } from 'react-icons/bs';
import { FaEquals, FaFutbol } from 'react-icons/fa';

interface H2HStats {
  totalMatches: number;
  homeWins: number;
  awayWins: number;
  draws: number;
  homeGoalsAvg: number;
  awayGoalsAvg: number;
  bttsPercentage: number;
  cornersAvg: {
    home: number;
    away: number;
  };
  cardsAvg: {
    home: number;
    away: number;
  };
  recentForm: {
    home: string;
    away: string;
  };
}

interface TeamStats {
  team: string;
  logo?: string;
  form: string;
  cornersPerGame: number;
  cardsPerGame: number;
  goalsFor: number;
  goalsAgainst: number;
  cleanSheets: number;
  averageGoals: number;
  bigChances: number;
  possession: number;
  shotsPerGame: number;
  last5Results: Array<{
    opponent: string;
    result: 'W' | 'D' | 'L';
    score: string;
    corners: number;
    cards: number;
  }>;
}

interface StatsIndicatorsProps {
  homeTeam: string;
  awayTeam: string;
  h2hStats: H2HStats;
  homeStats: TeamStats;
  awayStats: TeamStats;
  showDetailed?: boolean;
}

export function StatsIndicators({ 
  homeTeam, 
  awayTeam, 
  h2hStats, 
  homeStats, 
  awayStats,
  showDetailed = true 
}: StatsIndicatorsProps) {
  
  const StatRow = ({ 
    label, 
    homeValue, 
    awayValue, 
    icon, 
    isPercentage = false,
    isReverse = false,
    unit = ''
  }: {
    label: string;
    homeValue: number | string;
    awayValue: number | string;
    icon: React.ReactNode;
    isPercentage?: boolean;
    isReverse?: boolean;
    unit?: string;
  }) => {
    const homeNum = typeof homeValue === 'string' ? parseFloat(homeValue) : homeValue;
    const awayNum = typeof awayValue === 'string' ? parseFloat(awayValue) : awayValue;
    
    const getAdvantageColor = (home: number, away: number) => {
      if (Math.abs(home - away) < 0.1) return 'rgba(255, 193, 7, 0.1)'; // Empate
      if (isReverse) {
        return home < away ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)';
      }
      return home > away ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)';
    };

    const getValueColor = (home: number, away: number, isHome: boolean) => {
      if (Math.abs(home - away) < 0.1) return '#ff9800';
      const hasAdvantage = isReverse ? 
        (isHome ? home < away : away < home) : 
        (isHome ? home > away : away > home);
      return hasAdvantage ? '#4caf50' : '#f44336';
    };

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 1.5,
          px: 2,
          backgroundColor: getAdvantageColor(homeNum, awayNum),
          borderRadius: '8px',
          mb: 1,
          border: '1px solid rgba(255,255,255,0.1)',
          '&:hover': {
            backgroundColor: 'rgba(73, 251, 176, 0.05)',
            transform: 'translateX(2px)',
            transition: 'all 0.2s ease'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: '40px' }}>
          {icon}
        </Box>
        
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 'bold',
              color: getValueColor(homeNum, awayNum, true),
              minWidth: '80px',
              textAlign: 'center'
            }}
          >
            {typeof homeValue === 'number' ? homeValue.toFixed(isPercentage ? 0 : 1) : homeValue}{unit}
            {isPercentage && '%'}
          </Typography>
          
          <Typography variant="body2" sx={{ color: 'text.secondary', mx: 2, fontWeight: '500' }}>
            {label}
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 'bold',
              color: getValueColor(homeNum, awayNum, false),
              minWidth: '80px',
              textAlign: 'center'
            }}
          >
            {typeof awayValue === 'number' ? awayValue.toFixed(isPercentage ? 0 : 1) : awayValue}{unit}
            {isPercentage && '%'}
          </Typography>
        </Box>
      </Box>
    );
  };

  const FormDisplay = ({ form, team }: { form: string; team: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
        {team}:
      </Typography>
      {form.split('').map((result, index) => (
        <Chip
          key={index}
          label={result}
          size="small"
          sx={{
            minWidth: '24px',
            height: '24px',
            backgroundColor: 
              result === 'W' ? '#4caf50' : 
              result === 'D' ? '#ff9800' : '#f44336',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.7rem'
          }}
        />
      ))}
    </Box>
  );

  return (
    <Paper sx={{ 
      p: 3, 
      backgroundColor: 'rgba(73, 251, 176, 0.02)',
      border: '1px solid rgba(73, 251, 176, 0.1)',
      borderRadius: '12px'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: '#49fbb0', fontWeight: 'bold', mb: 1 }}>
          📊 Análise Estatística Detalhada
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Comparativo entre {homeTeam} vs {awayTeam}
        </Typography>
      </Box>

      {/* Head to Head */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ color: '#49fbb0', fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BsTrophy size={16} />
          Histórico Confrontos Diretos (H2H)
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: '8px' }}>
              <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                {h2hStats.homeWins}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Vitórias Casa
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'rgba(255, 193, 7, 0.1)', borderRadius: '8px' }}>
              <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                {h2hStats.draws}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Empates
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)', borderRadius: '8px' }}>
              <Typography variant="h4" sx={{ color: '#f44336', fontWeight: 'bold' }}>
                {h2hStats.awayWins}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Vitórias Fora
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center', mb: 2 }}>
          Últimos {h2hStats.totalMatches} jogos entre as equipes
        </Typography>
      </Box>

      <Divider sx={{ my: 2, borderColor: 'rgba(73, 251, 176, 0.2)' }} />

      {/* Estatísticas Zebradas */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ color: '#49fbb0', fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <MdSportsScore size={16} />
          Indicadores de Performance
        </Typography>

        <StatRow
          label="Gols Médios por Jogo"
          homeValue={homeStats.averageGoals}
          awayValue={awayStats.averageGoals}
          icon={<FaFutbol color="#49fbb0" size={16} />}
        />

        <StatRow
          label="Escanteios por Jogo"
          homeValue={homeStats.cornersPerGame}
          awayValue={awayStats.cornersPerGame}
          icon={<MdTrendingUp color="#49fbb0" size={16} />}
        />

        <StatRow
          label="Cartões por Jogo"
          homeValue={homeStats.cardsPerGame}
          awayValue={awayStats.cardsPerGame}
          icon={<MdTimer color="#49fbb0" size={16} />}
          isReverse={true}
        />

        <StatRow
          label="Posse de Bola Média"
          homeValue={homeStats.possession}
          awayValue={awayStats.possession}
          icon={<MdSportsScore color="#49fbb0" size={16} />}
          isPercentage={true}
          unit="%"
        />

        <StatRow
          label="Finalizações por Jogo"
          homeValue={homeStats.shotsPerGame}
          awayValue={awayStats.shotsPerGame}
          icon={<MdTrendingUp color="#49fbb0" size={16} />}
        />

        <StatRow
          label="Grandes Chances"
          homeValue={homeStats.bigChances}
          awayValue={awayStats.bigChances}
          icon={<BsTrophy color="#49fbb0" size={16} />}
        />

        <StatRow
          label="Jogos Sem Sofrer"
          homeValue={homeStats.cleanSheets}
          awayValue={awayStats.cleanSheets}
          icon={<MdTrendingDown color="#49fbb0" size={16} />}
        />
      </Box>

      <Divider sx={{ my: 2, borderColor: 'rgba(73, 251, 176, 0.2)' }} />

      {/* Forma Recente */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ color: '#49fbb0', fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BsCalendarEvent size={16} />
          Forma Recente (Últimos 5 Jogos)
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <FormDisplay form={homeStats.form} team={homeTeam} />
          <FormDisplay form={awayStats.form} team={awayTeam} />
        </Box>
      </Box>

      {showDetailed && (
        <>
          <Divider sx={{ my: 2, borderColor: 'rgba(73, 251, 176, 0.2)' }} />
          
          {/* Estatísticas H2H Específicas */}
          <Box>
            <Typography variant="subtitle1" sx={{ color: '#49fbb0', fontWeight: 'bold', mb: 2 }}>
              📈 Tendências H2H
            </Typography>
            
            <StatRow
              label="Ambas Marcam (%)"
              homeValue={h2hStats.bttsPercentage}
              awayValue={100 - h2hStats.bttsPercentage}
              icon={<FaFutbol color="#49fbb0" size={16} />}
              isPercentage={true}
            />

            <StatRow
              label="Média Gols H2H"
              homeValue={h2hStats.homeGoalsAvg}
              awayValue={h2hStats.awayGoalsAvg}
              icon={<MdSportsScore color="#49fbb0" size={16} />}
            />

            <StatRow
              label="Escanteios H2H"
              homeValue={h2hStats.cornersAvg.home}
              awayValue={h2hStats.cornersAvg.away}
              icon={<MdTrendingUp color="#49fbb0" size={16} />}
            />

            <StatRow
              label="Cartões H2H"
              homeValue={h2hStats.cardsAvg.home}
              awayValue={h2hStats.cardsAvg.away}
              icon={<MdTimer color="#49fbb0" size={16} />}
              isReverse={true}
            />
          </Box>
        </>
      )}
    </Paper>
  );
} 